<script setup lang="ts">
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue';
import { DateTime } from 'luxon';
import { useI18n } from 'vue-i18n';
import PersonIcon from '@/assets/icons/counterparty/card/private-person.svg';
import CounterpartyMasterCard from '@/modules/counterparty/counterparty-master/components/CounterpartyMasterCard.vue';
import {
  isPersonalContact,
  type CounterpartyContactItem,
} from '@/modules/counterparty/counterparty-master/types/contact-form.ts';
import UiTooltip from '@/modules/ui/components/ui/UiTooltip.vue';

const { item } = defineProps<{
  item: CounterpartyContactItem;
}>();

defineEmits<{
  edit: [];
  remove: [];
}>();

const { t } = useI18n();
const noteRef = useTemplateRef('note');
const noteTooltipDisabled = ref(true);
const personal = computed(() => isPersonalContact(item));
const comment = computed(() => item.comment.trim());
const fio = computed(() => item.fio.trim());
const noteText = computed(() => comment.value || fio.value);
const noteLabelKey = computed(() =>
  comment.value ? 'counterparty.master.steps.contact.card.note' : 'counterparty.master.steps.contact.field.fio'
);

const contactTooltipLines = computed(() => {
  const lines: { label: string; value: string }[] = [];
  const add = (key: string, value: string) => {
    if (value) {
      lines.push({ label: t(key), value });
    }
  };

  add('counterparty.master.steps.contact.card.contact', item.contact.trim());

  if (personal.value) {
    add('counterparty.master.steps.contact.field.fio', fio.value);

    if (item.birthDay) {
      const date = DateTime.fromSQL(item.birthDay);
      add(
        'counterparty.master.steps.contact.field.birthDay',
        date.isValid ? date.toFormat('dd.MM.yyyy') : item.birthDay
      );
    }

    add('counterparty.master.steps.contact.field.position', item.position?.label.trim() ?? '');
  }

  return lines;
});

function syncNoteTooltip() {
  const el = noteRef.value;
  noteTooltipDisabled.value = !(el && el.scrollHeight > el.clientHeight + 1);
}

watch(noteText, () => void nextTick(syncNoteTooltip));

onMounted(() => void nextTick(syncNoteTooltip));
</script>

<template>
  <CounterpartyMasterCard
    class="contact-card"
    @edit="$emit('edit')"
    @remove="$emit('remove')"
  >
    <template #title>{{ item.type.label }}</template>

    <div class="contact-body _flush">
      <UiTooltip
        class="contact-main"
        placement="bottom"
        align="left"
        :disabled="!personal"
      >
        <template #tooltip>
          <div class="contact-tooltip">
            <div
              v-for="line in contactTooltipLines"
              :key="line.label"
              class="contact-tooltip-item"
            >
              <p class="contact-tooltip-label">{{ line.label }}</p>
              <p class="contact-tooltip-value">{{ line.value }}</p>
            </div>
          </div>
        </template>

        <span
          v-if="personal"
          class="person-icon"
        >
          <PersonIcon />
        </span>

        <div class="contact-fields">
          <p class="meta">{{ $t('counterparty.master.steps.contact.card.contact') }}</p>
          <p class="value contact-value">{{ item.contact }}</p>
        </div>
      </UiTooltip>

      <div
        v-if="noteText"
        class="note-fields"
      >
        <p class="meta">{{ $t(noteLabelKey) }}</p>
        <UiTooltip
          class="clamp"
          :text="noteText"
          :disabled="noteTooltipDisabled"
          placement="bottom"
          align="left"
          @pointerenter="syncNoteTooltip"
        >
          <p
            ref="note"
            class="value note-value"
            title=""
          >
            {{ noteText }}
          </p>
        </UiTooltip>
      </div>
    </div>
  </CounterpartyMasterCard>
</template>

<style scoped lang="scss">
.contact-body {
  display: flex;
  box-sizing: border-box;
  width: calc(100% + 16px);
  min-width: 0;
  margin: -8px;
  padding: 8px;
  flex-direction: column;

  > :last-child {
    padding-right: 32px;
  }
}

.contact-main {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  gap: 8px;
}

.person-icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;

  :deep(svg) {
    display: block;
    width: 100%;
    height: 100%;
  }
}

.contact-fields,
.note-fields {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.clamp {
  display: block;
  min-width: 0;
}

.meta,
.value {
  margin: 0;
  min-width: 0;
  font-size: var(--font-size-s);
  line-height: var(--line-height-s);
}

.meta {
  color: var(--color-gray-500);
}

.value {
  color: var(--color-gray-900);
  overflow-wrap: anywhere;
  word-break: break-word;
}

.contact-value {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.note-value {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  pointer-events: none;
}

.contact-tooltip {
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
}

.contact-tooltip-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.contact-tooltip-label,
.contact-tooltip-value {
  margin: 0;
}

.contact-tooltip-label {
  font-weight: 500;
}

.contact-tooltip-value {
  color: var(--color-gray-1000);
}
</style>
